import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';
import { useFocusTrap, useScrollLock } from '../lib/useFocusTrap';
import { Button } from './Button';

const widths = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons. Right-aligned in a bordered footer. */
  footer?: ReactNode;
  size?: keyof typeof widths;
  /**
   * Set for a dialog the user must answer. Clicking the backdrop and pressing
   * Escape stop closing it. Use for destructive confirmations only — a modal you
   * cannot dismiss is hostile everywhere else.
   */
  dismissible?: boolean;
  /** Focus this on open instead of the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement>;
  className?: string;
}

/**
 * A modal dialog.
 *
 * Accessibility is not optional here: `role="dialog"`, `aria-modal`, a labelled
 * title, focus moved in on open and returned to the trigger on close, Escape to
 * dismiss, and Tab trapped inside. `useFocusTrap` does the work — read the comment
 * there before changing any of it.
 *
 * Rendered in a portal on `document.body` so it is never clipped by a parent's
 * `overflow: hidden`, which is the usual reason a dialog appears half off-screen.
 *
 * Never taller than the window. The title and footer stay put and the body scrolls
 * inside the panel, with its own scrollbar. A dialog that grew past the bottom of the
 * screen used to scroll the dark backdrop instead, which nobody reads as scrollable --
 * so whatever sat at the end of a long body was simply out of sight, and the dialog
 * looked like it had nothing more to offer.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  initialFocusRef,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useFieldId();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  useScrollLock(open);
  useFocusTrap(panelRef, open, {
    initialFocusRef,
    onEscape: dismissible ? onClose : undefined,
  });

  if (!open || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6">
      <div
        className="fixed inset-0 animate-fade-in bg-scrim backdrop-blur-[1px]"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-description` : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[calc(100dvh-2rem)] w-full animate-scale-in flex-col rounded-lg border bg-surface-raised shadow-overlay sm:max-h-[calc(100dvh-3rem)]',
          widths[size],
          className,
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b px-5 py-3.5">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {description && (
              <p id={`${id}-description`} className="mt-1 text-sm text-ink-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 rounded p-1 text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          )}
        </div>

        {children && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
            {children}
          </div>
        )}

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 rounded-b-lg border-t bg-surface-sunken px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmModalProps extends Omit<ModalProps, 'footer' | 'children'> {
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  loading?: boolean;
  destructive?: boolean;
  /**
   * What will happen, spelled out. A design constraint of this product: every
   * destructive or state-changing action shows what will happen before it happens
   * (conversion, closure, verification, visibility change).
   */
  consequences?: ReactNode;
}

export function ConfirmModal({
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  loading,
  destructive,
  consequences,
  ...modalProps
}: ConfirmModalProps) {
  return (
    <Modal
      {...modalProps}
      size={modalProps.size ?? 'sm'}
      footer={
        <>
          <Button variant="ghost" onClick={modalProps.onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {consequences}
    </Modal>
  );
}
